import { useEffect, useRef } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { RefreshCw, X } from 'lucide-react';
import { useUiStore } from '../lib/store';
import styles from '../App.module.css';

/**
 * Apply a new build automatically — unless doing so would cost the user work.
 *
 * The original rule was "never reload without being asked", which protects a
 * half-written draft but has a worse failure mode: someone who never notices
 * the toast runs an old bundle indefinitely and reports shipped features as
 * missing. That happened.
 *
 * So the rule is now conditional on what is actually at risk. With no composer
 * open there is nothing to lose and the swap is silent. With a draft in
 * progress the prompt still appears and the decision is still the user's, which
 * is the only case the original policy was really protecting.
 *
 * The reload itself is safe for read state: everything the UI shows comes from
 * the server or localStorage, so a swap costs at most a scroll position.
 */
const AUTO_APPLY_DELAY_MS = 1500;

export default function UpdateToast() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW();

  const composerOpen = useUiStore((s) => s.composer.open);
  // A ref so the effect does not re-arm its timer every render.
  const applied = useRef(false);

  useEffect(() => {
    if (!needRefresh || composerOpen || applied.current) return;
    // A short delay so the swap never lands in the same frame as a click the
    // user has just made.
    const timer = window.setTimeout(() => {
      applied.current = true;
      void updateServiceWorker(true);
    }, AUTO_APPLY_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [needRefresh, composerOpen, updateServiceWorker]);

  // Only ever visible while a draft is open — otherwise the effect above has
  // already taken care of it.
  if (!needRefresh || !composerOpen) return null;

  return (
    <div className={styles.toast} role="status" data-motion="toast">
      <RefreshCw size={15} className="lucide" aria-hidden="true" />
      <span>Update ready · finish your draft first</span>
      <button type="button" className="kiln-button" onClick={() => void updateServiceWorker(true)}>
        Reload
      </button>
      <button
        type="button"
        className={styles.toastDismiss}
        onClick={() => setNeedRefresh(false)}
        aria-label="Dismiss"
      >
        <X size={14} className="lucide" />
      </button>
    </div>
  );
}
